const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const toDateOrNull = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isInstallationRecord = (row = {}) => {
    if (row.isSiteInspection) return false;
    if (row.isDemonstration) return Boolean(row.installationDateFrom || row.installationDateTo);
    const requested = row.requestedMovementType || row.movementType;
    return requested === 'Installed' || requested === 'Exchanged - Installed';
};

export const INSTALLATION_ALERT_META = {
    all: { label: { en: 'All', es: 'Todas' }, tone: 'neutral', priority: 6 },
    overdue: { label: { en: 'Overdue', es: 'Atrasadas' }, tone: 'danger', priority: 0 },
    today: { label: { en: 'Today / under 24 h', es: 'Hoy / menos de 24 h' }, tone: 'urgent', priority: 1 },
    upcoming: { label: { en: 'Next 72 h', es: 'Próximas 72 h' }, tone: 'warning', priority: 2 },
    ready: { label: { en: 'Ready to close', es: 'Listas para cerrar' }, tone: 'success', priority: 3 },
    scheduled: { label: { en: 'Scheduled', es: 'Programadas' }, tone: 'info', priority: 4 },
    unscheduled: { label: { en: 'No date', es: 'Sin fecha' }, tone: 'neutral', priority: 5 },
    not_applicable: { label: { en: 'Not applicable', es: 'No aplica' }, tone: 'neutral', priority: 7 }
};

export function getInstallationAlert(row = {}, nowValue = new Date()) {
    if (!isInstallationRecord(row)) return { code: 'not_applicable', ...INSTALLATION_ALERT_META.not_applicable };

    const progress = Number(row.checklistProgress || 0);
    const checklistReady = progress >= 100 || row.checklistStatus === 'ready_to_close';
    if (checklistReady) return { code: 'ready', ...INSTALLATION_ALERT_META.ready };

    const now = toDateOrNull(nowValue) || new Date();
    const start = toDateOrNull(row.installationDateFrom);
    const end = toDateOrNull(row.installationDateTo) || start;
    if (!start && !end) return { code: 'unscheduled', ...INSTALLATION_ALERT_META.unscheduled };

    const scheduledStart = start || end;
    const deadline = end || scheduledStart;
    if (deadline.getTime() < now.getTime()) {
        return {
            code: 'overdue',
            ...INSTALLATION_ALERT_META.overdue,
            minutes: Math.max(0, Math.floor((now.getTime() - deadline.getTime()) / 60000))
        };
    }

    const minutesUntil = Math.max(0, Math.ceil((scheduledStart.getTime() - now.getTime()) / 60000));
    if (minutesUntil <= 24 * 60) {
        return { code: 'today', ...INSTALLATION_ALERT_META.today, minutes: minutesUntil };
    }
    if (minutesUntil <= 72 * 60) {
        return { code: 'upcoming', ...INSTALLATION_ALERT_META.upcoming, minutes: minutesUntil };
    }
    return { code: 'scheduled', ...INSTALLATION_ALERT_META.scheduled, minutes: minutesUntil };
}

const compactDuration = (minutes, language) => {
    const safe = Math.max(0, Math.round(Number(minutes) || 0));
    const days = Math.floor(safe / 1440);
    const hours = Math.floor((safe % 1440) / 60);
    const mins = safe % 60;
    const parts = [];
    if (days) parts.push(language === 'es' ? `${days} d` : `${days} d`);
    if (hours) parts.push(language === 'es' ? `${hours} h` : `${hours} h`);
    if (!days && mins) parts.push(`${mins} min`);
    return parts.join(' ') || '0 min';
};

export function formatInstallationTiming(row = {}, language = 'en', nowValue = new Date()) {
    const alert = getInstallationAlert(row, nowValue);
    if (alert.code === 'not_applicable') return null;
    if (alert.code === 'unscheduled') return language === 'es' ? 'Programación requerida' : 'Scheduling required';
    if (alert.code === 'ready') return language === 'es' ? 'Lista para cerrar' : 'Ready to close';
    if (alert.code === 'overdue') {
        return language === 'es'
            ? `Atrasada ${compactDuration(alert.minutes, language)}`
            : `Overdue ${compactDuration(alert.minutes, language)}`;
    }
    if (alert.code === 'today') {
        return language === 'es'
            ? `Faltan ${compactDuration(alert.minutes, language)}`
            : `Due in ${compactDuration(alert.minutes, language)}`;
    }
    return language === 'es'
        ? `Faltan ${compactDuration(alert.minutes, language)}`
        : `Due in ${compactDuration(alert.minutes, language)}`;
}

export function filterByInstallationAlert(rows = [], filter = 'all', nowValue = new Date()) {
    if (!filter || filter === 'all') return rows;
    return rows.filter((row) => getInstallationAlert(row, nowValue).code === filter);
}

export function sortByInstallationPriority(rows = [], nowValue = new Date()) {
    return [...rows].sort((left, right) => {
        const leftAlert = getInstallationAlert(left, nowValue);
        const rightAlert = getInstallationAlert(right, nowValue);
        if (leftAlert.priority !== rightAlert.priority) return leftAlert.priority - rightAlert.priority;
        const leftDate = toDateOrNull(left.installationDateFrom || left.createdAt);
        const rightDate = toDateOrNull(right.installationDateFrom || right.createdAt);
        return (leftDate?.getTime() || Number.MAX_SAFE_INTEGER) - (rightDate?.getTime() || Number.MAX_SAFE_INTEGER);
    });
}

export const INSTALLATION_ALERT_FILTERS = ['all', 'overdue', 'today', 'upcoming', 'ready', 'unscheduled'];
